import React from 'react';
import styled from 'styled-components';
import {Button, ButtonDropdownField, Detail, DropdownList, EMPHASIS, Icon, SubHeading, Text} from '@forecast-it/design-system';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_CARD_SURFACE_BACKGROUND,
	FEATURE_FLAG_LINK_TEXT,
	FEATURE_FLAG_NEUTRAL_FILL,
} from '../featureFlagSurface';
import {parseCsv} from './csvParse';

/**
 * Largest file this tab will read. A list of company ids is tiny, so anything approaching this is a
 * mistake — and reading it would block the tab while it is decoded.
 */
const MAX_CSV_BYTES = 5 * 1024 * 1024;

/**
 * Rows of the file drawn as a preview, header included. Enough to confirm the right column is chosen
 * without pushing the modal's footer off screen.
 */
const PREVIEW_ROW_LIMIT = 4;

/** How many skipped values a report names before trailing off. */
const SKIP_EXAMPLE_LIMIT = 4;

const UploadPanel = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	padding: 32px 20px;
	border: 1px dashed ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
	text-align: center;
`;

/*
 * The design system has no file input, so the visible control is a real button painted to look
 * like a primary one, which forwards its click to the hidden native input via a ref. A button is
 * natively focusable and keyboard-activatable, unlike a label wrapping a hidden input — the input
 * itself is never seen or reached directly.
 */
const ChooseFileButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 40px;
	margin-top: 4px;
	padding: 0 18px;
	border: none;
	border-radius: 4px;
	background: ${FEATURE_FLAG_LINK_TEXT};
	color: ${FEATURE_FLAG_CARD_SURFACE_BACKGROUND};
	cursor: pointer;
`;

const HiddenFileInput = styled.input`
	display: none;
`;

const FileStrip = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 12px 16px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-radius: 4px;
`;

const FileIdentity = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
`;

const FileNameCell = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-top: 16px;
`;

const MicroLabel = styled.span`
	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const PreviewCard = styled.div`
	margin-top: 16px;
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
	overflow: hidden;
`;

const PreviewHead = styled.div`
	padding: 9px 14px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};

	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const PreviewScroll = styled.div`
	overflow-x: auto;
`;

const PreviewGrid = styled.div<{$columns: number}>`
	display: grid;
	grid-template-columns: repeat(${props => props.$columns}, minmax(120px, 1fr));
	min-width: min-content;
`;

const PreviewCell = styled.span<{$selected: boolean}>`
	padding: 8px 14px;
	white-space: nowrap;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	background: ${props => (props.$selected ? FEATURE_FLAG_NEUTRAL_FILL : 'transparent')};

	/* The chosen column is the one thing this preview exists to confirm, so it carries the accent. */
	> * {
		color: ${props => (props.$selected ? FEATURE_FLAG_LINK_TEXT : 'inherit')};
	}
`;

const Strip = styled.div`
	display: flex;
	gap: 10px;
	margin-top: 14px;
	padding: 12px 14px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-radius: 4px;
`;

const StripIcon = styled.span`
	flex: none;
	margin-top: 1px;
`;

/** `1 row · 3 columns`, counting body rows only — the header is not data. */
function fileSummary(rows: readonly string[][], columnCount: number): string {
	const bodyCount = Math.max(0, rows.length - 1);
	return `${bodyCount} ${bodyCount === 1 ? 'row' : 'rows'} · ${columnCount} ${columnCount === 1 ? 'column' : 'columns'}`;
}

/** A header's own text, or its position when the file left it blank. */
function columnLabel(header: string, index: number): string {
	return header.trim() === '' ? `Column ${index + 1}` : header;
}

/** The first few values, trailing off when there are more. */
function examples(values: readonly string[]): string {
	const shown = values.slice(0, SKIP_EXAMPLE_LIMIT).join(', ');
	return values.length > SKIP_EXAMPLE_LIMIT ? `${shown}…` : shown;
}

function unmatchedMessage(values: readonly string[]): string {
	return values.length === 1
		? `1 value did not match a company and will be skipped: ${examples(values)}`
		: `${values.length} values did not match a company and will be skipped: ${examples(values)}`;
}

function alreadyGrantedMessage(names: readonly string[]): string {
	return names.length === 1
		? `1 company in this file already has the flag: ${examples(names)}`
		: `${names.length} companies in this file already have the flag: ${examples(names)}`;
}

export interface RolloutCsvTabProps {
	/** Name of the loaded file; null when none is loaded. */
	fileName: string | null;
	/** The parsed file, row 0 being the header; null when none is loaded. */
	rows: readonly string[][] | null;
	/** Which column the operator has picked as the company id. */
	columnIndex: number;
	onColumnIndexChange: (columnIndex: number) => void;
	/** A file was read. `rows` is never empty. */
	onFileLoaded: (fileName: string, rows: string[][]) => void;
	/** A file was rejected. The message is ready to display as it stands. */
	onFileError: (message: string) => void;
	/** Drop the loaded file and go back to asking for one. */
	onClearFile: () => void;
	/** Rejection message to show while no file is loaded; null when there is none. */
	errorMessage: string | null;
	/** Raw cell values that named no company. */
	unmatchedValues: readonly string[];
	/** Names of companies in the file that already hold the flag. */
	alreadyGrantedNames: readonly string[];
}

export function RolloutCsvTab({
	fileName,
	rows,
	columnIndex,
	onColumnIndexChange,
	onFileLoaded,
	onFileError,
	onClearFile,
	errorMessage,
	unmatchedValues,
	alreadyGrantedNames,
}: RolloutCsvTabProps): JSX.Element {
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const readerRef = React.useRef<FileReader | null>(null);
	const readGenerationRef = React.useRef(0);

	// A read outlives this component: switching tabs unmounts the tab while the reader keeps running
	// against the modal's callbacks, and a late result would clobber state the operator has since
	// changed. Aborting on the way out is what keeps a stale read from applying.
	React.useEffect(
		() => () => {
			readerRef.current?.abort();
			readGenerationRef.current += 1;
		},
		[]
	);

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files == null ? null : event.target.files[0] ?? null;
		// Clearing the input lets the same file be chosen again. A change event does not fire for a
		// value the input already holds, so a rejected file could otherwise never be retried.
		event.target.value = '';
		if (file == null) {
			return;
		}

		// A read already in flight is superseded. Bumping the generation means a callback that already
		// slipped past abort is ignored, so the file that finishes last cannot beat the file chosen last.
		readerRef.current?.abort();
		readGenerationRef.current += 1;
		const generation = readGenerationRef.current;

		if (file.size > MAX_CSV_BYTES) {
			onFileError('That file is too large to read here — 5 MB is the limit.');
			return;
		}
		const reader = new FileReader();
		readerRef.current = reader;
		reader.onerror = () => {
			if (generation !== readGenerationRef.current) {
				return;
			}
			onFileError('That file could not be read.');
		};
		reader.onload = () => {
			if (generation !== readGenerationRef.current) {
				return;
			}
			const parsed = parseCsv(String(reader.result ?? ''));
			if (parsed.length === 0) {
				onFileError('That file has no rows.');
				return;
			}
			onFileLoaded(file.name, parsed);
		};
		reader.readAsText(file);
	};

	if (rows == null) {
		return (
			<UploadPanel>
				<SubHeading>Upload a CSV of companies</SubHeading>
				<Detail color="medium">
					The file needs a header row and a column holding the company ID. Everything else is ignored.
				</Detail>
				<ChooseFileButton onClick={() => fileInputRef.current?.click()} type="button">
					Choose CSV file
				</ChooseFileButton>
				<HiddenFileInput accept=".csv,text/csv" onChange={handleFileChange} ref={fileInputRef} type="file" />
				{errorMessage == null ? null : (
					<Strip>
						<StripIcon>
							<Icon icon="warning" size="s" />
						</StripIcon>
						<Detail color="medium">{errorMessage}</Detail>
					</Strip>
				)}
			</UploadPanel>
		);
	}

	const header = rows[0] ?? [];
	const previewRows = rows.slice(0, PREVIEW_ROW_LIMIT);
	// The widest row, not the header alone: a body row that runs on past the header still holds real
	// data, and a column that fell off the header is otherwise neither selectable nor visible.
	const columnCount = rows.reduce((widest, row) => Math.max(widest, row.length), 0);
	const columnIndexes = Array.from({length: columnCount}, (_, index) => index);

	return (
		<div>
			<FileStrip>
				<FileIdentity>
					<FileNameCell>
						<Text>{fileName}</Text>
					</FileNameCell>
					<Detail color="medium">{fileSummary(rows, columnCount)}</Detail>
				</FileIdentity>
				<Button emphasis={EMPHASIS.LOW} onPress={onClearFile}>
					Replace file
				</Button>
			</FileStrip>

			<Section>
				<MicroLabel>
					<Detail size="2">COMPANY ID COLUMN</Detail>
				</MicroLabel>
				<DropdownList>
					<DropdownList.Trigger>
						<ButtonDropdownField aria-label={'Company ID column'} width={'100%'}>
							{columnLabel(header[columnIndex] ?? '', columnIndex)}
						</ButtonDropdownField>
					</DropdownList.Trigger>
					<DropdownList.Content useTriggerWidth>
						{columnIndexes.map(index => (
							<DropdownList.Item key={index} onClick={() => onColumnIndexChange(index)}>
								{columnLabel(header[index] ?? '', index)}
							</DropdownList.Item>
						))}
					</DropdownList.Content>
				</DropdownList>
			</Section>

			<PreviewCard>
				<PreviewHead>
					<Detail size="2">FILE PREVIEW — FIRST ROWS</Detail>
				</PreviewHead>
				<PreviewScroll>
					<PreviewGrid $columns={Math.max(1, columnCount)}>
						{previewRows.map((row, rowIndex) =>
							columnIndexes.map(cellIndex => (
								<PreviewCell $selected={cellIndex === columnIndex} key={`${rowIndex}-${cellIndex}`}>
									{rowIndex === 0 ? (
										<Detail size="2">{row[cellIndex] ?? ''}</Detail>
									) : (
										<Text>{row[cellIndex] ?? ''}</Text>
									)}
								</PreviewCell>
							))
						)}
					</PreviewGrid>
				</PreviewScroll>
			</PreviewCard>

			{unmatchedValues.length === 0 ? null : (
				<Strip>
					<StripIcon>
						<Icon icon="warning" size="s" />
					</StripIcon>
					<Detail color="medium">{unmatchedMessage(unmatchedValues)}</Detail>
				</Strip>
			)}

			{alreadyGrantedNames.length === 0 ? null : (
				<Strip>
					<StripIcon>
						<Icon icon="info" size="s" />
					</StripIcon>
					<Detail color="medium">{alreadyGrantedMessage(alreadyGrantedNames)}</Detail>
				</Strip>
			)}
		</div>
	);
}
